<script setup lang="ts">
import { computed, defineProps, type PropType, ref, onMounted, watch } from 'vue'
import type OreConversion from '@/types/OreConversion.ts'
import { formatShort } from '../../../../../models/formatNumber.ts'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
  conversion: {
    type: Object as PropType<OreConversion>,
    required: true
  },
  objectImage: {
    type: String,
    required: true
  },
  width: {
    type: Number,
    required: true
  },
  height: {
    type: Number,
    required: true
  },
  top: {
    type: Number,
    required: true
  },
  left: {
    type: Number,
    required: true
  },
  debug: {
    type: Boolean,
    required: false,
    default: false
  }
})

const hover = ref(false)
const tooltipPos = ref({ top: 0, left: 0 })

const updateTooltipPosition = () => {
  // Fixed position relative to the component
  let topPos = props.top + 1
  let leftPos = props.left - 5.5

  // Get viewport dimensions in rem
  const viewportWidth =
    window.innerWidth / parseFloat(getComputedStyle(document.documentElement).fontSize)
  const viewportHeight =
    window.innerHeight / parseFloat(getComputedStyle(document.documentElement).fontSize)

  // Tooltip dimensions
  const tooltipWidth = 5 // 5rem
  const tooltipHeight = 2 // estimated tooltip height

  // Check right edge
  if (leftPos + tooltipWidth > viewportWidth) {
    leftPos = props.left - tooltipWidth + 1
  }

  // Check left edge
  if (leftPos < 0) {
    leftPos = props.left
  }

  // Check bottom edge
  if (topPos + tooltipHeight > viewportHeight) {
    topPos = props.top - tooltipHeight
  }

  tooltipPos.value = { top: topPos, left: leftPos }
}

// Calculate initial position once
onMounted(() => {
  updateTooltipPosition()
})

// Update position when component props change
watch([() => props.top, () => props.left], () => {
  updateTooltipPosition()
})

const conveyorColor = computed(() => {
  if (props.conversion.capacity == 0) {
    return '#0a0c10'
  }
  return '#252b35'
})

const oreSpeed = computed(() => {
  if (props.conversion.capacity <= 0) {
    return 0 // No speed if ore processed is zero or negative
  }

  // Calculate logarithmic speed inversely
  const logValue = Math.log10(1000 / props.conversion.capacity)

  // Define a base speed that increases with ore processed
  return Math.max(1, logValue) // Ensure a minimum speed of 0.3
})

// Update tooltip position when hovering
const handleMouseEnter = () => {
  hover.value = true
  // No position update here, using pre-calculated position
}
</script>

<template>
  <svg
    :class="{
      absolute: true,
      border: debug
    }"
    :style="
      'top:' + top + 'rem;left:' + left + 'rem;width:' + width + 'rem;height:' + height + 'rem;'
    "
    xmlns="http://www.w3.org/2000/svg"
  >
    <!-- Background rail -->
    <rect
      x="0"
      y="0"
      width="1rem"
      :height="height + 'rem'"
      :fill="conveyorColor"
      @mouseenter="handleMouseEnter"
      @mouseout="hover = false"
    />

    <image
      v-if="conversion.capacity > 0"
      :href="props.objectImage"
      x="0"
      y="0"
      width="1rem"
      height="1rem"
      @mouseenter="handleMouseEnter"
      @mouseout="hover = false"
    >
      <!-- Animate the downward movement -->
      <animate
        attributeName="y"
        :values="'0;' + (height - 1) + 'rem'"
        keyTimes="0;1"
        :dur="oreSpeed + 's'"
        repeatCount="indefinite"
      />
      <!-- Animate the leftward movement -->
    </image>
  </svg>

  <div
    v-if="props.conversion.capacity > 0 && hover"
    class="absolute text-green-500 border p-1 rounded border-metal-600 bg-metal-800 text-center"
    :style="'top:' + tooltipPos.top + 'rem;left:' + tooltipPos.left + 'rem; width:5rem;z-index:18;'"
  >
    {{ formatShort(props.conversion.capacity) }}<small class="text-subtext"> {{ t('resources.conveyor.perWeek') }}</small>
  </div>
</template>

<style scoped lang="scss"></style>
